'use client';

import type { AgentGenerationStep } from '@/lib/agent-kit/types';
import { AgentProgress } from '@/components/ai/AgentProgress';
import { resumeRefineStepCopy } from './step-graph';
import type { ResumeRefineStepKey, ResumeRefineTrace } from './types';

/**
 * The refine flow's progress checklist.
 *
 * Wraps the shared AgentProgress with this flow's copy, its per-step summaries
 * and its reasoning panel, so the caller mounts one component and passes the
 * hook's state straight through.
 *
 * The "why" panel leads with the gaps. That is the one thing a user genuinely
 * needs from this run: not what we rewrote, but what we refused to claim on
 * their behalf — and therefore what they will be asked about and cannot bluff.
 */

export interface ResumeRefineProgressProps {
  steps: AgentGenerationStep<ResumeRefineStepKey>[];
  status: 'idle' | 'running' | 'ready' | 'error';
  error?: string | null;
  trace?: ResumeRefineTrace | null;
  className?: string;
}

const asStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

function summarizeStep(step: AgentGenerationStep<ResumeRefineStepKey>): string | null {
  const output = step.output;
  if (!output) return null;
  if (step.status === 'SKIPPED') return typeof output.note === 'string' ? output.note : null;

  switch (step.step_key) {
    case 'EXTRACT_TARGET': {
      const role = typeof output.role === 'string' ? output.role : 'the role';
      const hard = asStrings(output.hardRequirements).length;
      return `${role} — ${hard} hard requirement${hard === 1 ? '' : 's'}`;
    }
    case 'MAP_EVIDENCE': {
      const gaps = asStrings(output.gaps).length;
      const strong = typeof output.strong === 'number' ? output.strong : 0;
      return `${strong} strong match${strong === 1 ? '' : 'es'}, ${gaps} gap${gaps === 1 ? '' : 's'}`;
    }
    case 'REWRITE': {
      const roles = typeof output.rolesRewritten === 'number' ? output.rolesRewritten : 0;
      return `Summary and ${roles} role${roles === 1 ? '' : 's'} retargeted`;
    }
    case 'CRITIQUE': {
      const failed = asStrings(output.failedChecks);
      return failed.length === 0
        ? 'Nothing invented, nothing overstated'
        : `Flagged: ${failed.join(', ')}`;
    }
    case 'POLISH': {
      const applied = asStrings(output.applied).length;
      return `${applied} fix${applied === 1 ? '' : 'es'} applied`;
    }
    default:
      return null;
  }
}

function RefineDetail({ trace }: { trace: ResumeRefineTrace }) {
  const { evidence, critique, finalFabricatedTerms, changeNotes } = trace.detail;
  const gaps = evidence?.gaps ?? [];

  return (
    <>
      <div>
        <p className="text-overline text-muted-foreground">What this resume cannot claim</p>
        {gaps.length === 0 ? (
          <p className="text-caption text-muted-foreground">
            Nothing the target asked for was missing from your experience.
          </p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {gaps.map((gap) => (
              <li key={gap} className="text-caption text-muted-foreground">
                {gap}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1 text-caption text-muted-foreground">
          These were left out on purpose. You would have to defend them in an interview.
        </p>
      </div>

      {evidence?.transferableAngles?.length ? (
        <div>
          <p className="text-overline text-muted-foreground">Honest angles on those gaps</p>
          <ul className="mt-1 space-y-0.5">
            {evidence.transferableAngles.map((angle) => (
              <li key={angle} className="text-caption text-muted-foreground">
                {angle}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {changeNotes.length > 0 ? (
        <div>
          <p className="text-overline text-muted-foreground">What changed</p>
          <ul className="mt-1 space-y-0.5">
            {changeNotes.map((note) => (
              <li key={note} className="text-caption text-muted-foreground">
                {note}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {critique ? (
        <div>
          <p className="text-overline text-muted-foreground">Independent check</p>
          <p className="text-caption text-muted-foreground">
            Risk {critique.risk.toLowerCase()}, targeting {critique.targeting.toLowerCase()}.
          </p>
          {finalFabricatedTerms.length > 0 ? (
            <p className="text-caption text-destructive">
              Still unsupported by your original resume: {finalFabricatedTerms.join(', ')}. Check
              these before you send it.
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export function ResumeRefineProgress({
  steps,
  status,
  error,
  trace,
  className,
}: ResumeRefineProgressProps) {
  return (
    <AgentProgress<ResumeRefineStepKey>
      steps={steps}
      copy={resumeRefineStepCopy}
      status={status}
      error={error}
      doneTitle="Your resume is retargeted"
      summarize={summarizeStep}
      detail={trace ? <RefineDetail trace={trace} /> : null}
      className={className}
    />
  );
}
